import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import {
  DemanderKbn,
  DemanderRelationship,
  RetireFlowReason,
  YesOrNoFlug,
} from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkStepper from "../../components/atoms/TkStepper";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { API_RET005_BuildReqParams } from "../../utils/buildRequestParams";
import { IAPI_RET005_Request } from "../../type/apiType";
import { httpAPI_RET005 } from "./api/request.formdata";
import { fakeTableList, steps, schema1, schema2 } from "./parts";
import _ from "lodash";
import "./_style.scss";

/**
 * SCRR411105 退職手当金の請求手続き（請求者情報の登録）
 * 退職手当金請求者が請求者区分を登録する。
 * @return SCRR411105
 */
const SCRR411105 = () => {
  const navigate = useNavigate();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const isUserTypeKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;
  const context = businessState.job_35;

  const [stateTableList, setStateTableList] = useState<any>([]);
  const [open, setOpen] = useState(false);
  const [demanderKbn, setDemanderKbn] = useState("");
  const [relationship, setRelationship] = useState<any>(null);
  const [livelihoodFromRetireeFlg, setLivelihoodFromRetireeFlg] = useState("");
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [stateSchema, setStateSchema] = useState(schema1);

  // 初期表示
  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let retireeName = resArr.find((item) => item.keyword === "retireeName")!;
    let demanderKbn = resArr.find((item) => item.keyword === "demanderKbn")!;
    let demanderRelationship = resArr.find(
      (item) => item.keyword === "demanderRelationship"
    )!;

    // 退職者名
    retireeName.showLine = isUserTypeKiko;
    retireeName.contractorNumber = context?.staffNameKanji;

    let beravedFlg =
      context?.retirementProcedureReason === RetireFlowReason["職員の死亡"] ||
      context?.retirementProcedureReason === RetireFlowReason["死亡による退職"];

    // 請求者区分
    if (!beravedFlg) {
      let newRadioList = demanderKbn.radioPropsList;
      newRadioList[2].disabled = true;
      demanderKbn.radioPropsList = newRadioList;
    } else {
      let newRadioList = demanderKbn.radioPropsList;
      newRadioList[0].disabled = true;
      demanderKbn.radioPropsList = newRadioList;
    }
    demanderKbn.setDefaultValue = context?.demanderKbn;
    demanderKbn.onGetValue = (event: any) => {
      if (event === DemanderKbn["退職者本人"]) {
        setRelationship(null);
        setLivelihoodFromRetireeFlg("");
      }
      setDemanderKbn(event);
    };

    // 続柄行
    context?.demanderRelationship &&
      (demanderRelationship.setDefaultValue = {
        selectValue: context?.demanderRelationship,
        inputValue: context?.demanderRelationshipOthers,
      });
    demanderRelationship.onGetValue = (event: any) => {
      setRelationship(event.selectValue);
    };

    // 退職者からの生計維持の有無（※1）
    let livelihoodFromRetireeFlg = resArr.find(
      (item) => item.keyword === "livelihoodFromRetireeFlg"
    )!;
    livelihoodFromRetireeFlg.setDefaultValue =
      context?.livelihoodFromRetireeFlg;
    livelihoodFromRetireeFlg.onGetValue = (event: any) => {
      setLivelihoodFromRetireeFlg(event);
    };

    // あなたと同順位の請求権保持者の有無（※2）
    resArr.find((item) => item.keyword === "demanderFlg")!.setDefaultValue =
      context?.demanderFlg;
    setStateTableList([...resArr]);

    context?.demanderKbn && setDemanderKbn(context?.demanderKbn);
    context?.demanderRelationship &&
      setRelationship(context?.demanderRelationship);
    context?.livelihoodFromRetireeFlg &&
      setLivelihoodFromRetireeFlg(context?.livelihoodFromRetireeFlg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let demanderRelationship = resArr.find(
      (item) => item.keyword === "demanderRelationship"
    )!;
    let notRetiree = resArr.filter(
      (item) =>
        item.keyword === "demanderRelationship" ||
        item.keyword === "livelihoodFromRetireeFlg" ||
        item.keyword === "demanderFlg"
    )!;
    let heir = resArr.find((item) => item.keyword === "heir")!;
    let bereavedFamily = resArr.find(
      (item) => item.keyword === "bereavedFamily"
    )!;
    let spouseOnFamilyRegister = resArr.find(
      (item) => item.keyword === "spouseOnFamilyRegister"
    )!;
    let commonLawWifeOrHusband = resArr.find(
      (item) => item.keyword === "commonLawWifeOrHusband"
    )!;
    let livelihoodRelation = resArr.find(
      (item) => item.keyword === "livelihoodRelation"
    )!;
    let notLivelihoodRelation = resArr.find(
      (item) => item.keyword === "notLivelihoodRelation"
    )!;
    let heirRequiredDocuments = resArr.find(
      (item) => item.keyword === "heirRequiredDocuments"
    )!;

    let notRetireeFlg =
      !!demanderKbn && demanderKbn !== DemanderKbn["退職者本人"];
    let spouseFlg =
      !!relationship && relationship === DemanderRelationship["戸籍上の配偶者"];
    let commonLowFlg =
      !!relationship && relationship === DemanderRelationship["内縁の妻又は夫"];
    notRetiree.forEach((item) => {
      item.showLine = notRetireeFlg;
      if (demanderKbn === DemanderKbn["退職者本人"])
        item.setDefaultValue = undefined;
    });
    setStateSchema(notRetireeFlg ? { ...schema1, ...schema2 } : schema1);
    heir.showLine = demanderKbn === DemanderKbn["相続人"];
    bereavedFamily.showLine = demanderKbn === DemanderKbn["遺族"];
    if (demanderKbn === DemanderKbn["相続人"]) {
      heirRequiredDocuments.showLine = true;
      livelihoodRelation.showLine = false;
      notLivelihoodRelation.showLine = false;
      bereavedFamily.showLine = false;
      commonLawWifeOrHusband.showLine = false;
      spouseOnFamilyRegister.showLine = false;
    } else {
      heirRequiredDocuments.showLine = false;
      spouseOnFamilyRegister.showLine = notRetireeFlg && spouseFlg;
      commonLawWifeOrHusband.showLine = notRetireeFlg && commonLowFlg;
      livelihoodRelation.showLine =
        notRetireeFlg &&
        !spouseFlg &&
        !commonLowFlg &&
        livelihoodFromRetireeFlg === YesOrNoFlug["有"];
      notLivelihoodRelation.showLine =
        notRetireeFlg &&
        !spouseFlg &&
        !commonLowFlg &&
        livelihoodFromRetireeFlg === YesOrNoFlug["無"];
    }

    let newRadioList = demanderRelationship.radioPropsList;
    newRadioList[1].disabled = demanderKbn === DemanderKbn["相続人"];
    demanderRelationship.radioPropsList = newRadioList;
    if (
      demanderKbn === DemanderKbn["相続人"] &&
      relationship === DemanderRelationship["内縁の妻又は夫"]
    ) {
      demanderRelationship.setDefaultValue = {
        selectValue: null,
        inputValue: null,
      };
    }

    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demanderKbn, relationship, livelihoodFromRetireeFlg]);

  const makeHttpObj = (data: any): IAPI_RET005_Request => {
    const reqObj: IAPI_RET005_Request = {
      applyId: data.applyId, // 申請id
      tempSaveStep: 2, // 一時保存ステップ
      businessType: businessState.common?.businessType, // 業務種別
      contractorNo: isUserTypeKiko
        ? businessState.common?.contractorNo!
        : state.authInfo?.contractorNo!, // 共済契約者番号
      staffNo: data.staffNo!, // 退職者法人職員番号
      demanderName: data.demanderName, // 請求者名
      demanderNameKana: data.demanderNameKana, // 請求者ｶﾅ
      demanderKbn: data.demanderKbn, // 請求者区分
      demanderRelationship: data.demanderRelationship, // 請求者続柄
      demanderRelationshipOthers: data.demanderRelationshipOthers, // 請求者その他続柄
      demanderBirth: data.demanderBirth, // 生年月日
      tel: data.tel, // 電話番号
      demanderEmail: data.demanderEmail, // 請求者メールアドレス
      agentEmail: data.agentEmail, // 代行者メールアドレス
      demanderZipcode: data.demanderZipcode, // 請求者住所（郵便番号）
      demanderPrefectureCd: data.demanderPrefectureCd, // 請求者住所（都道府県）
      demanderCity: data.demanderCity, // 請求者住所（市区町村）
      demanderAddressOthers: data.demanderAddressOthers, // 請求者住所（その他）
      nowZipcode:
        demanderKbn === ""
          ? null
          : demanderKbn === "1"
          ? data.demanderZipcode
          : data.retirementZipcode, // 現住所（郵便番号）
      nowPrefectureCd:
        demanderKbn === ""
          ? null
          : demanderKbn === "1"
          ? data.demanderPrefectureCd
          : data.retirementPrefectureCd, // 現住所（都道府県）
      nowAddressCity:
        demanderKbn === ""
          ? null
          : demanderKbn === "1"
          ? data.demanderCity
          : data.retirementAddressCity, // 現住所（市区町村）
      nowAddressOthers:
        demanderKbn === ""
          ? null
          : demanderKbn === "1"
          ? data.demanderAddressOthers
          : data.retirementAddressOthers, // 現住所（その他）
      domesticResidenceFlg: data.domesticResidenceFlg, // 請求者国内居住有無
      retirementYearFirstZipcode: data.retirementYearFirstZipcode, // 退職年度初日住所（郵便番号）
      retirementYearFirstPrefectureCd: data.retirementYearFirstPrefectureCd, // 退職年度初日住所（都道府県）
      retirementYearFirstAddressCity: data.retirementYearFirstAddressCity, // 退職年度初日住所（市区町村）
      retirementYearAddressOthers: data.retirementYearAddressOthers, // 退職年度初日住所（その他）
      retirementReason: data.retirementReason, // 退職理由（請求者記載）
      nRetireKbn: data.retireKbn, // 退職手当等の退職の区分
      firstJoinDate: data.firstJoinDate, // 退職手当等についての勤続期間（自）
      retireDate: data.retireDate, // 退職手当等についての勤続期間（至）
      livingAssistanceFlg: data.livingAssistanceFlg, // 生活扶助の有無
      paymentConfirmDate: data.paymentConfirmDate, // 退職手当等の支払いを受けることになった年月日
      demanderFlg: data.demanderFlg, // あなたと同順位の請求権保持者の有無
      accountNameKana: data.accountNameKana, // 口座名義ｶﾅ
      depositType: data.depositType, // 預金種目
      accountNo: data.accountNo, // 口座番号
      livelihoodFromRetireeFlg: data.livelihoodFromRetireeFlg, // 退職者からの生計維持の有無
      identificationDocsKind: data.identificationDocsKind, // 本人確認書類の種類
      addPaymentApplyFlg: data.addPaymentApplyFlg, // 追加支給申請フラグ
      addPaymentReason: data.addPaymentReason, // 追加支給理由
      addPaymentAmt: data.addPaymentAmt, // 追加支給金額
      totalPaymentAmtAfterAdd: data.totalPaymentAmtAfterAdd, // 支給額(追加支給後)
      pastLosInCurrentYearOtherFrom: data.totalCurrentYearLosTo, // 本年中に支払を受けた他の退職手当等についての勤続期間に通算された前の勤続期間（至）
      pastLosInCurrentYearOtherTo: data.currentYearLosFrom, // 本年中に支払を受けた他の退職手当等についての勤続期間（自）
      currentYearLosTo: data.currentYearLosTo, // 本年中に支払を受けた他の退職手当等についての勤続期間（至）
      currentYearPaymentDecisionDate: data.currentYearPaymentDecisionDate, // 本年中に支払を受けた他の退職手当等の支払を受けることとなった年月日
      currentYearRetirementAllowanceAmt: data.currentYearRetirementAllowanceAmt, // 本年中に支払を受けた他の退職手当等の収入金額
      currentYearWithholdingAmt: data.currentYearWithholdingAmt, // 本年中に支払を受けた他の退職手当等の源泉徴収額
      currentYearScCityAmt: data.currentYearScCityAmt, // 本年中に支払を受けた他の退職手当等の特別徴収税額（市町村民税）
      currentYearScPrefectureAmt: data.currentYearScPrefectureAmt, // 本年中に支払を受けた他の退職手当等特別徴収税額（道府県民税）
      currentYearGetPaidDate: data.currentYearGetPaidDate, // 本年中に支払を受けた他の退職手当等の支払いを受けた年月日
      currentYearRetirementKbn: data.currentYearRetirementKbn, // 本年中に支払を受けた他の退職手当等の退職の区分
      currentYearPayerZipcode: data.currentYearPayerZipcode, // 本年中に支払を受けた他の退職手当等の支払者の住所(郵便番号)
      currentYearPayerPrefectureCd: data.currentYearPayerPrefectureCd, // 本年中に支払を受けた他の退職手当等の支払者の住所(都道府県)
      currentYearPayerAddressCity: data.currentYearPayerAddressCity, // 本年中に支払を受けた他の退職手当等の支払者の住所(市区町村)
      currentYearPayerAddressOthers: data.currentYearPayerAddressOthers, // 本年中に支払を受けた他の退職手当等の支払者の住所(その他)
      currentYearPayerName: data.past4yearsPayerName, // 本年中に支払を受けた他の退職手当等の支払者の名称(氏名)
      past4yearsLosFrom: data.past4yearsLosFrom, // 前年以前4年内（その年に確定拠出年金法に基づく老齢給付金として支給される一時金の支払いを受ける場合には、14年内）の退職手当等についての勤続期間（自）
      past4yearsLosTo: data.past4yearsLosTo, // 前年以前4年内（その年に確定拠出年金法に基づく老齢給付金として支給される一時金の支払いを受ける場合には、14年内）の退職手当等についての勤続期間（至）
      past4yearsPaymentDecisionDate: data.past4yearsPaymentDecisionDate, // 前年以前4年内の退職手当等の支払を受けることとなった年月日
      past4yearsRetirementAllowanceAmt: data.past4yearsRetirementAllowanceAmt, // 前年以前4年内の退職手当等の収入金額
      past4yearsWithholdingAmt: data.past4yearsWithholdingAmt, // 前年以前4年内の退職手当等の源泉徴収額
      past4yearsScCityAmt: data.past4yearsScPrefectureAmt, // 前年以前4年内の退職手当等の特別徴収税額（市町村民税）
      past4yearsScPrefectureAmt: data.past4yearsScCityAmt, // 前年以前4年内の退職手当等の特別徴収税額（道府県民税）
      past4yearsGetPaidDate: data.past4yearsGetPaidDate, // 前年以前4年内の退職手当等の支払いを受けた年月日
      past4yearsRetirementKbn: data.past4yearsRetirementKbn, // 前年以前4年内の退職手当等の退職の区分
      past4yearsPayerZipcode: data.past4yearsPayerZipcode, // 前年以前4年内の退職手当等の支払者の住所(郵便番号)
      past4yearsPayerPrefectureCd: data.past4yearsPayerPrefectureCd, // 前年以前4年内の退職手当等の支払者の住所(都道府県)
      past4yearsPayerAddressCity: data.past4yearsPayerAddressCity, // 前年以前4年内の退職手当等の支払者の住所(市区町村)
      past4yearsPayerAddressOthers: data.past4yearsPayerAddressOthers, // 前年以前4年内の退職手当等の支払者の住所(その他)
      past4yearsPayerName: data.past4yearsPayerName, // 前年以前4年内の退職手当等の支払者の名称(氏名)
      retirementFormApplyId: data.retirementFormApplyId, // 被共済職員退職届申請id
      page1Kbn: data.page1Kbn, // 1枚目源泉徴収票・特別徴収票の区分
      page1PaymentAmt: data.page1PaymentAmt, // 1枚目の支払金額
      page1WithholdingAmt: data.page1WithholdingAmt, // 1枚目の源泉徴収税額
      page1ScAmtCity: data.page1ScAmtCity, // 1枚目の特別徴収税額（市町村民税）
      page1ScAmtPrefecture: data.page1ScAmtPrefecture, // 1枚目の特別徴収税額（道府県民税）
      page1LengthOfService: data.page1LengthOfService, // 1枚目の勤続年数
      page1EmployedDate: data.page1EmployedDate, // 1枚目の就職年月日
      page1RetiredDate: data.page1RetiredDate, // 1枚目の退職年月日
      page2Kbn: data.page2Kbn, // 2枚目源泉徴収票・特別徴収票の区分
      page2PaymentAmt: data.page2PaymentAmt, // 2枚目の支払金額
      page2WithholdingAmt: data.page2WithholdingAmt, // 2枚目の源泉徴収税額
      page2ScAmtCity: data.page2ScAmtCity, // 2枚目の特別徴収税額（市町村民税）
      page2ScAmtPrefecture: data.page2ScAmtPrefecture, // 2枚目の特別徴収税額（道府県民税）
      page2LengthOfService: data.page2LengthOfService, // 2枚目の勤続年数
      page2EmployedDate: data.page2EmployedDate, // 2枚目の就職年月日
      page2RetiredDate: data.page2RetiredDate, // 2枚目の退職年月日
      page3Kbn: data.page3Kbn, // 3枚目源泉徴収票・特別徴収票の区分
      page3PaymentAmt: data.page3PaymentAmt, // 3枚目の支払金額
      page3WithholdingAmt: data.page3WithholdingAmt, // 3枚目の源泉徴収税額
      page3ScAmtCity: data.page3ScAmtCity, // 3枚目の特別徴収税額（市町村民税）
      page3ScAmtPrefecture: data.page3ScAmtPrefecture, // 3枚目の特別徴収税額（道府県民税）
      page3LengthOfService: data.page3LengthOfService, // 3枚目の勤続年数
      page3EmployedDate: data.page3EmployedDate, // 3枚目の就職年月日
      page3RetiredDate: data.page3RetiredDate, // 3枚目の退職年月日
      contractorName: data.contractorName, // 契約者（法人）名
      fileList: data.fileList,
      receptionDate: "", // 受付日
      screenId: "SCRR411105", // 画面id
    };
    return API_RET005_BuildReqParams(reqObj);
  };

  const handleSubmit = async (data: any, index: any) => {
    // 続柄変更チェック
    const fileList = context?.fileList;
    let newFileList;
    const beforeRelationship = context?.demanderRelationship;
    const afterRelationship = relationship;
    if (afterRelationship !== beforeRelationship) {
      if (beforeRelationship === "2") {
        newFileList = fileList?.filter(
          (item: any) => !["10", "21", "22"].includes(item.formClass)
        );
      } else if (
        !!beforeRelationship &&
        beforeRelationship !== "1" &&
        ["1", "2"].includes(afterRelationship)
      ) {
        newFileList = fileList?.filter((item: any) => item.formClass !== "10");
      }
    }

    let mergedData = {
      ...context,
      ...data,
      demanderKbn: data.demanderKbn,
      demanderRelationship: data.demanderRelationship?.selectValue,
      demanderFlg: data.demanderFlg,
      livelihoodFromRetireeFlg: livelihoodFromRetireeFlg,
      demanderRelationshipOthers: data.demanderRelationship?.inputValue,
      fileList: newFileList && newFileList.length > 0 ? newFileList : fileList,
    };
    // 持回り情報.請求者区分が画面に入力された請求者区分が一致しているかどうかにより、請求者関連の情報をクリアする
    if (context?.demanderKbn && context?.demanderKbn !== data.demanderKbn) {
      mergedData = {
        ...mergedData,
        demanderNameKana: null,
        demanderName: null,
        demanderBirth: null,
        demanderZipcode: null,
        demanderPrefectureCd: null,
        demanderCity: null,
        demanderAddressOthers: null,
        demanderEmail: null,
        agentEmail: null,
        retirementReason: null,
      };
    }
    if (index === 0) {
      // 一時保存
      try {
        const res = await httpAPI_RET005({
          ...makeHttpObj(mergedData),
        });
        if (res.resultCd === "I0000") {
          setState({
            ...state,
            errorMsgId: "",
          });
          setBusinessState({
            ...businessState,
            job_35: {
              ...mergedData,
              tempSaveStep: 2,
              applyId: res.applyId,
            },
          });

          setOpen(true);
        } else {
          setState({
            ...state,
            errorMsgId: res.resultCd,
          });
        }
      } catch (error) {
        navigate("/error");
      }
    } else {
      // 次へ
      setBusinessState({
        ...businessState,
        job_35: mergedData,
      });

      navigate("/SCRR411106");
    }
  };

  return (
    <Box className="SCRR411105" data-testid="SCRR411105PageForTest">
      <TkStepper
        steps={steps}
        activeStep={1}
        savaStep={context?.tempSaveStep}
      />

      <TkFakeTable
        list={stateTableList}
        ruleSchema={stateSchema}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
        buttonGroup={<TkBackButton />}
        onCheckAndSubmit={handleSubmit}
      />

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRR411105;
